import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TransferDialog from "@/components/TransferDialog";
import AddPedigreeAncestorDialog from "@/components/AddPedigreeAncestorDialog";
import PedigreeAdditionPreview from "@/components/PedigreeAdditionPreview";
import AdminTools from "@/components/AdminTools";
import AppShell from "@/components/AppShell";
import FirstDogCelebration from "@/components/FirstDogCelebration";
import UpgradeNudge from "@/components/UpgradeNudge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DogIcon as Dog, VideoCameraIcon as VideoCamera } from "@phosphor-icons/react";
import { formatDate, formatApiError } from "@/lib/utils-app";
import { API } from "@/lib/api";
import { toast } from "sonner";
import PdfModal from "@/components/PdfModal";

import Card from "@/components/dog-detail/Card";
import ParentLink from "@/components/dog-detail/ParentLink";
import SubList from "@/components/dog-detail/SubList";
import DogEvents from "@/components/dog-detail/DogEvents";
import InbreedingCard from "@/components/dog-detail/InbreedingCard";
import BreedingPanel from "@/components/dog-detail/BreedingPanel";
import ListDogDialog from "@/components/dog-detail/ListDogDialog";
import HealthVault from "@/components/HealthVault";
import DogNotificationsPanel from "@/components/DogNotificationsPanel";
import CertificatePurchaseDialog from "@/components/dog-detail/CertificatePurchaseDialog";
import DogSidebar from "@/components/dog-detail/DogSidebar";
import CareTab from "@/components/dog-detail/CareTab";
import MarketplaceListingDialog from "@/components/dog-detail/MarketplaceListingDialog";
import DNAVerificationCard from "@/components/dog-detail/DNAVerificationCard";
import SaleContractCard from "@/components/dog-detail/SaleContractCard";
import BreedingDirectoryPanel from "@/components/dog-detail/BreedingDirectoryPanel";

export default function DogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dog, setDog] = useState(null);
  const [tab, setTab] = useState("overview");
  const [sire, setSire] = useState(null);
  const [dam, setDam] = useState(null);
  const [breeder, setBreeder] = useState(null);
  const [events, setEvents] = useState([]);
  const [coi, setCoi] = useState(null);
  const [copied, setCopied] = useState(false);
  const [certAccess, setCertAccess] = useState(null); // {granted, reason}
  const [certPrice, setCertPrice] = useState(null); // effective $ amount for certificate_purchase
  const [purchasing, setPurchasing] = useState(false);
  const [breedingListing, setBreedingListing] = useState(null);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [certPromo, setCertPromo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [marketplaceDialogOpen, setMarketplaceDialogOpen] = useState(false);
  const [listingCheckout, setListingCheckout] = useState(false);
  const [featureCheckout, setFeatureCheckout] = useState(false);
  // Pedigree-certificate PDF modal — replaces the /pdf-viewer route
  // navigation. See PdfModal.jsx for why we use an overlay instead.
  const [showPedigreePdf, setShowPedigreePdf] = useState(false);

  // Deep-link from `/breeder/analytics` Quick-win pills: `?edit=price`
  // auto-opens the marketplace edit dialog the moment the dog row
  // finishes loading. Once consumed, we strip the param so a refresh
  // doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Smart marketplace-listing handler:
   *   - If the dog isn't listed yet, kick off the paid checkout flow
   *     ($9.99 / 60 days, free for Basic/Pro/Elite subscribers).
   *   - If already listed, open the existing dialog so the owner can
   *     edit price / description / status / delist.
   */
  const handleListingClick = async () => {
    if (dog?.for_sale) {
      setMarketplaceDialogOpen(true);
      return;
    }
    // Under-8-week puppy gate. Backend `billing/routes.py` enforces
    // the same rule (so a hand-crafted POST can't bypass), but we
    // pre-flight client-side so the breeder gets a confirm dialog
    // BEFORE we send them to Stripe — much friendlier than a 400
    // toast on the return leg.
    const ageDays = (() => {
      if (!dog?.dob) return null;
      const s = String(dog.dob).slice(0, 10);
      const parsed = new Date(`${s}T00:00:00Z`);
      if (Number.isNaN(parsed.getTime())) return null;
      const now = new Date();
      const utcToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
      return Math.floor((utcToday - parsed.getTime()) / (1000 * 60 * 60 * 24));
    })();
    const isUnder8wk = ageDays !== null && ageDays < 56;
    let under8wkAttestation = false;
    if (isUnder8wk) {
      const ready = 56 - ageDays;
      const ok = window.confirm(
        `This puppy is ${ageDays} day${ageDays === 1 ? "" : "s"} old — ` +
          "under the 8-week (56-day) statutory minimum transfer age in many U.S. states.\n\n" +
          "You can publish the listing now to start a waitlist or take deposits, " +
          "but you must NOT transfer custody before the puppy reaches 8 weeks of age " +
          `(about ${ready} day${ready === 1 ? "" : "s"} from today). The Buy button ` +
          "stays disabled until the puppy ages in.\n\n" +
          "Tap OK to confirm and continue, or Cancel to wait until the puppy is older.",
      );
      if (!ok) return;
      under8wkAttestation = true;
    }
    setListingCheckout(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "dog_marketplace_listing",
        origin_url: webOrigin(),
        dog_id: id,
        ...(under8wkAttestation ? { under_8wk_attestation: true } : {}),
      });
      if (data.free) {
        toast.success(
          data.reason?.startsWith("included_with")
            ? "Listed — included with your plan."
            : "Listed on the marketplace for 60 days."
        );
        const fresh = await api.get(`/dogs/${id}`);
        setDog(fresh.data);
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setListingCheckout(false);
    }
  };

  /** Promote an active listing to the top of marketplace search for 30
   *  days. Backed by the `marketplace_feature` checkout package. Free
   *  for admins; otherwise routes through Stripe.
   */
  const handleFeatureClick = async () => {
    setFeatureCheckout(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "marketplace_feature",
        origin_url: webOrigin(),
        dog_id: id,
      });
      if (data.free) {
        toast.success("Promoted — your listing is now at the top of the marketplace for 30 days.");
        const fresh = await api.get(`/dogs/${id}`);
        setDog(fresh.data);
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFeatureCheckout(false);
    }
  };
  const [addAncestorOpen, setAddAncestorOpen] = useState(false);
  const [addAncestorRelation, setAddAncestorRelation] = useState("sire");
  const [resubmitTarget, setResubmitTarget] = useState(null); // {id, entry, relation}
  const [myPendingCount, setMyPendingCount] = useState(0);
  const [pendingPreviewId, setPendingPreviewId] = useState(null);
  const [previewAdditionId, setPreviewAdditionId] = useState(null);
  const [myOpenSubmissions, setMyOpenSubmissions] = useState([]);

  const publicUrl = `${webOrigin()}/d/${id}`;
  // OG-rich backend share URL — when pasted into Facebook / iMessage /
  // Slack the crawler gets server-rendered OG tags + the per-dog
  // 1200×630 share-card PNG (photo, name, price). Human browsers are
  // instantly redirected to the SPA page.
  const shareUrl = `${API}/public/dogs/${dog?.slug || id}/share`;
  const certUrl = `${API}/dogs/${id}/certificate.pdf?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;
  const regCertUrl = `${API}/dogs/${id}/registration-certificate.pdf?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: dog?.name || "Dog", url: shareUrl });
        return;
      }
    } catch { /* fall through */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — photo + details preview on social.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const handleCertificateClick = () => {
    if (!id) return;
    if (certAccess?.granted) {
      // Open the in-page PDF modal (portal-based overlay). Replaces
      // the previous /pdf-viewer route navigation, which left users
      // stranded on iOS PWA because Safari's history stack quirks
      // made `navigate(-1)` unreliable in standalone mode.
      setShowPedigreePdf(true);
      return;
    }
    // Not yet purchased — open the dialog so user can apply a promo code first
    setCertDialogOpen(true);
  };

  const handleCertificateCheckout = async () => {
    setPurchasing(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "certificate_purchase",
        origin_url: webOrigin(),
        dog_id: id,
        ...(certPromo?.valid ? { promo_code: certPromo.code } : {}),
      });
      if (data.free) {
        toast.success("Pedigree certificate unlocked");
        setCertAccess({
          granted: true,
          reason: data.already ? "purchased" : (data.promo ? "purchased" : "pro"),
        });
        setPurchasing(false);
        setCertDialogOpen(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setPurchasing(false);
    } catch (err) {
      toast.error(formatApiError(err));
      setPurchasing(false);
    }
  };

  const load = async () => {
    try {
      const r = await api.get(`/dogs/${id}`);
      setDog(r.data);
      if (r.data.sire_id) api.get(`/dogs/${r.data.sire_id}`).then((x) => setSire(x.data)).catch(() => {});
      if (r.data.dam_id) api.get(`/dogs/${r.data.dam_id}`).then((x) => setDam(x.data)).catch(() => {});
      if (r.data.breeder_id) {
        api.get(`/breeders/${r.data.breeder_id}`).then((x) => setBreeder(x.data)).catch(() => {});
        // Last-touch kennel-referral attribution: visiting a dog
        // page credits the dog's kennel for the buyer's eventual
        // Buyer Pass subscription, scoped to a 30-day TTL.
        import("@/lib/referral-attribution").then((m) => m.recordKennelVisit(r.data.breeder_id));
      }
      api.get(`/dogs/${id}/events`).then((x) => setEvents(x.data)).catch(() => setEvents([]));
      api.get(`/dogs/${id}/inbreeding?generations=5`).then((x) => setCoi(x.data)).catch(() => setCoi(null));
      api.get(`/dogs/${id}/certificate-access`).then((x) => setCertAccess(x.data)).catch(() => setCertAccess({ granted: false, reason: "none" }));
      api.get("/billing/package-price/certificate_purchase").then((x) => setCertPrice(x.data.amount)).catch(() => {});
      api.get(`/breeding/for-dog/${id}`).then((x) => setBreedingListing(x.data.listing)).catch(() => setBreedingListing(null));
      api
        .get("/pedigree-additions/mine")
        .then((x) => {
          const open = (x.data || []).filter(
            (a) =>
              a.attached_dog_id === id &&
              (a.status === "pending_admin_approve" || a.status === "revisions_requested")
          );
          setMyPendingCount(open.length);
          setPendingPreviewId(open[0]?.id || null);
          setMyOpenSubmissions(open);
        })
        .catch(() => setMyPendingCount(0));
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fire-and-forget view-ping so the breeder analytics dashboard knows
  // this listing was actually looked at. The endpoint is idempotent
  // per (viewer, dog, day) so refreshes don't inflate counts, and
  // owners viewing their own listing are not credited server-side.
  useEffect(() => {
    if (!id) return;
    api.post(`/public/analytics/listings/${id}/view-ping`).catch(() => {});
  }, [id]);

  // Honor the `?edit=price` deep-link from the Quick-win pills on the
  // breeder analytics dashboard. Wait until the dog row is loaded so
  // the dialog has its props. Strip the param after opening so a
  // refresh doesn't keep popping the dialog.
  useEffect(() => {
    if (!dog) return;
    const edit = searchParams.get("edit");
    if (edit === "price") {
      setMarketplaceDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }, [dog, searchParams, setSearchParams]);

  const onDelete = async () => {
    try {
      await api.delete(`/dogs/${id}`);
      toast.success("Dog removed");
      navigate("/dogs");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addSubItem = async (endpoint, data) => {
    try {
      const res = await api.post(`/dogs/${id}/${endpoint}`, data);
      toast.success("Added");
      load();
      // Return the new row so callers (SubList) can immediately attach
      // an uploaded file via the same flow.
      return res.data;
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  const delSubItem = async (endpoint, itemId) => {
    try {
      await api.delete(`/dogs/${id}/${endpoint}/${itemId}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (notFound)
    return (
      <AppShell>
        <div
          className="max-w-xl mx-auto bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center"
          data-testid="dog-not-found"
        >
          <Dog size={48} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-4" />
          <h1 className="font-spectral text-3xl tracking-tight mb-2">Dog not found</h1>
          <p className="text-[#6B6B6B] mb-6">
            This dog may have been deleted, or the link in your message is no longer valid.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/dogs">
              <Button className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full">
                Back to dogs
              </Button>
            </Link>
            <Link to="/inbox">
              <Button variant="outline" className="rounded-full border-[#E5E5E5]">
                Open inbox
              </Button>
            </Link>
          </div>
        </div>
      </AppShell>
    );

  if (!dog)
    return (
      <AppShell>
        <div className="text-[#6B6B6B]">Loading…</div>
      </AppShell>
    );

  const isOwnerOrAdmin = dog.created_by === user?.id || user?.role === "admin";

  return (
    <AppShell>
      <FirstDogCelebration dog={dog} />
      {/* Soft Care+ nudge for owners on the free $25 registration tier.
          Auto-hides once they subscribe (UpgradeNudge calls /care/status
          on mount and renders nothing if active=true). Each dog has its
          own dismissal key so a user can dismiss-then-unsubscribe-then-
          re-see-the-banner safely. */}
      {isOwnerOrAdmin && <UpgradeNudge dogId={id} dogName={dog.name} />}
      {/* Working-video nudge: owners who already have photos but no
          working clips get a one-click prompt to add one. Hidden the
          moment any video exists. Discoverable from the dog detail
          page (where the owner already lives) so they don't have to
          go hunting for the feature inside the edit form. */}
      {isOwnerOrAdmin && (dog.photos?.length || 0) >= 1 && (dog.videos?.length || 0) === 0 && (
        <div
          className="mb-6 rounded-xl border border-crimson/30 bg-[#FFF5F5] p-4 sm:p-5 flex items-start gap-3"
          data-testid="dog-detail-video-nudge"
        >
          <span className="p-2 rounded-full bg-crimson/10 flex-shrink-0">
            <VideoCamera size={20} weight="duotone" className="text-crimson" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-spectral text-[17px] sm:text-lg leading-tight text-ink">
              Add a working-dog video for {dog.name}.
            </p>
            <p className="text-[12.5px] sm:text-[13px] text-[#3F3F3F] mt-1 leading-snug">
              Buyers research a sire/dam before committing to a litter. A 10–30 second
              clip of bitework, hunt drive, agility, or clean obedience answers the
              question photos can&apos;t: <strong>&ldquo;Is the temperament real?&rdquo;</strong>
            </p>
            <Link
              to={`/dogs/${id}/edit?focus=videos`}
              className="mt-2.5 inline-flex items-center gap-1 h-9 px-4 rounded-full bg-ink text-white text-[12.5px] font-bold hover:bg-crimson transition"
              data-testid="dog-detail-add-video-btn"
            >
              <VideoCamera size={13} weight="bold" /> Add a video
            </Link>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Mobile-first ordering: the tabs (Overview / Events / Titles
            / Awards / Health / Ownership / Dog Log / Breeding) are the
            primary content, but the DogSidebar has ~12 action buttons.
            Put tabs above the sidebar on mobile (`order-first`) and
            restore the natural sidebar-left layout from md: up. */}
        <aside className="md:col-span-4 order-last md:order-first">
          <DogSidebar
            dog={dog}
            id={id}
            user={user}
            breeder={breeder}
            copied={copied}
            onShare={share}
            purchasing={purchasing}
            certAccess={certAccess}
            certPrice={certPrice}
            onCertificateClick={handleCertificateClick}
            regCertUrl={regCertUrl}
            publicUrl={publicUrl}
            onOpenTransfer={() => setTransferDialogOpen(true)}
            onOpenListing={handleListingClick}
            listingBusy={listingCheckout}
            onFeature={handleFeatureClick}
            featureBusy={featureCheckout}
            onDelete={onDelete}
            onDogPatched={(patch) => setDog((d) => (d ? { ...d, ...patch } : d))}
          />
        </aside>

        <div className="md:col-span-8 min-w-0 order-first md:order-none">
          <Tabs value={tab} onValueChange={setTab}>
            {/* Mobile: clean 3-column grid (rows always align even with
                a partial last row). Tablet+: single-row pill bar. */}
            <TabsList
              data-testid="dog-tabs-scroll"
              className="bg-white border border-[#E5E5E5] rounded-2xl md:rounded-full p-1 grid grid-cols-3 md:flex md:flex-nowrap md:whitespace-nowrap md:w-max md:max-w-none gap-1 md:gap-0 w-full text-[12px] md:text-sm h-auto"
            >
              <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full md:flex-none w-full md:w-auto min-w-0">Overview</TabsTrigger>
              <TabsTrigger value="events" data-testid="tab-events" className="rounded-full md:flex-none w-full md:w-auto min-w-0">
                Events
                {events.length > 0 && (
                  <span className="ml-1 bg-[#C1272D]/10 text-crimson rounded-full px-2 text-xs font-semibold">
                    {events.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="titles" data-testid="tab-titles" className="rounded-full md:flex-none w-full md:w-auto min-w-0">Titles</TabsTrigger>
              <TabsTrigger value="awards" data-testid="tab-awards" className="rounded-full md:flex-none w-full md:w-auto min-w-0">Awards</TabsTrigger>
              <TabsTrigger value="health" data-testid="tab-health" className="rounded-full md:flex-none w-full md:w-auto min-w-0">Health</TabsTrigger>
              <TabsTrigger value="ownership" data-testid="tab-ownership" className="rounded-full md:flex-none w-full md:w-auto min-w-0">Ownership</TabsTrigger>
              {isOwnerOrAdmin && (
                <TabsTrigger value="care" data-testid="tab-care" className="rounded-full md:flex-none w-full md:w-auto min-w-0">
                  Dog Log
                </TabsTrigger>
              )}
              {(isOwnerOrAdmin || breedingListing) && (
                <TabsTrigger value="breeding" data-testid="tab-breeding" className="rounded-full md:flex-none w-full md:w-auto min-w-0">
                  Breeding
                  {breedingListing && (
                    <span className="ml-1 bg-[#C1272D]/10 text-crimson rounded-full px-2 text-xs font-semibold">
                      Live
                    </span>
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview" className="mt-6 space-y-6">
              <DNAVerificationCard
                dog={dog}
                isOwner={isOwnerOrAdmin}
                onDogPatched={(patch) =>
                  setDog((d) => (d ? { ...d, ...patch } : d))
                }
              />
              <SaleContractCard
                dog={dog}
                isOwner={isOwnerOrAdmin}
                currentUserEmail={user?.email}
              />
              <BreedingDirectoryPanel dog={dog} isOwner={isOwnerOrAdmin} />
              <Card title="Parents">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <ParentLink label="Sire" dog={sire} />
                    {!sire && isOwnerOrAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setAddAncestorRelation("sire"); setAddAncestorOpen(true); }}
                        className="mt-2 rounded-full border-dashed border-[#C9A227] text-[#8A6A00] hover:bg-[#FBF7EC] text-xs"
                        data-testid="add-sire-btn"
                      >
                        + Add known sire
                      </Button>
                    )}
                  </div>
                  <div>
                    <ParentLink label="Dam" dog={dam} />
                    {!dam && isOwnerOrAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setAddAncestorRelation("dam"); setAddAncestorOpen(true); }}
                        className="mt-2 rounded-full border-dashed border-[#C9A227] text-[#8A6A00] hover:bg-[#FBF7EC] text-xs"
                        data-testid="add-dam-btn"
                      >
                        + Add known dam
                      </Button>
                    )}
                  </div>
                </div>
                {myPendingCount > 0 && (
                  <div
                    className="text-xs text-[#8A6A00] bg-[#FBF7EC] border border-[#E5D08A] rounded-lg px-3 py-2 mt-3 space-y-2"
                    data-testid="pedigree-pending-note"
                  >
                    {myOpenSubmissions.map((s) => (
                      <div key={s.id} className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {s.status === "revisions_requested" ? (
                            <>
                              <p className="font-semibold">Admin requested revisions:</p>
                              <p className="text-ink mt-0.5 whitespace-pre-wrap">
                                {s.revision_notes}
                              </p>
                            </>
                          ) : (
                            <p>
                              Submitted — awaiting admin review.
                              {s.resubmit_count > 0 && (
                                <span className="ml-1 text-ink">
                                  (resubmitted {s.resubmit_count}×)
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => setPreviewAdditionId(s.id)}
                            className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#8A6A00] hover:underline"
                            data-testid={`pedigree-pending-preview-btn-${s.id}`}
                          >
                            Preview tentative pedigree →
                          </button>
                          {s.status === "revisions_requested" && (
                            <button
                              type="button"
                              onClick={() => {
                                const entry = (s.entries || [])[0] || {};
                                setResubmitTarget({
                                  id: s.id,
                                  entry,
                                  relation: entry.relation || "sire",
                                });
                              }}
                              className="text-[11px] font-bold uppercase tracking-[0.15em] text-white bg-[#C9A227] hover:bg-[#A88500] rounded-full px-3 py-1"
                              data-testid={`pedigree-resubmit-btn-${s.id}`}
                            >
                              Resubmit with fixes →
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              {coi && (
                <InbreedingCard
                  coi={coi}
                  onRecompute={async (opts) => {
                    try {
                      const { data } = await api.get(`/dogs/${id}/inbreeding`, { params: opts });
                      setCoi(data);
                    } catch (err) {
                      toast.error(formatApiError(err));
                    }
                  }}
                />
              )}
              {dog.notes && (
                <Card title="Notes">
                  <p className="text-ink leading-relaxed whitespace-pre-wrap">{dog.notes}</p>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="events" className="mt-6">
              <DogEvents events={events} />
            </TabsContent>

            <TabsContent value="titles" className="mt-6">
              <SubList
                title="Titles"
                items={dog.titles || []}
                fields={[
                  { name: "title", label: "Title", required: true },
                  { name: "awarded_by", label: "Awarded by" },
                  { name: "date", label: "Date", type: "date" },
                ]}
                render={(t) => (
                  <>
                    <span className="bg-[#C1272D]/10 text-crimson border border-[#C1272D]/20 rounded-full px-3 py-1 text-xs font-semibold">
                      {t.title}
                    </span>
                    {t.awarded_by && <span className="text-sm text-[#6B6B6B] ml-2">by {t.awarded_by}</span>}
                    {t.date && <span className="text-xs text-[#6B6B6B] ml-2">{formatDate(t.date)}</span>}
                  </>
                )}
                onAdd={(data) => addSubItem("titles", data)}
                onDelete={(itemId) => delSubItem("titles", itemId)}
                addLabel="Add title"
                dogId={id}
                attachmentKind="titles"
                canEdit={isOwnerOrAdmin}
                onAttachmentsChanged={load}
              />
            </TabsContent>

            <TabsContent value="awards" className="mt-6">
              <SubList
                title="Awards"
                items={dog.awards || []}
                fields={[
                  { name: "name", label: "Award", required: true },
                  { name: "event", label: "Event" },
                  { name: "date", label: "Date", type: "date" },
                  { name: "placement", label: "Placement" },
                ]}
                render={(a) => (
                  <>
                    <div className="font-medium">{a.name}</div>
                    <div className="text-xs text-[#6B6B6B]">
                      {a.event} {a.placement && `· ${a.placement}`} {a.date && `· ${formatDate(a.date)}`}
                    </div>
                  </>
                )}
                onAdd={(data) => addSubItem("awards", data)}
                onDelete={(itemId) => delSubItem("awards", itemId)}
                addLabel="Add award"
                dogId={id}
                attachmentKind="awards"
                canEdit={isOwnerOrAdmin}
                onAttachmentsChanged={load}
              />
            </TabsContent>

            <TabsContent value="health" className="mt-6 space-y-6">
              {/* Health Vault — owner-only aggregated read view with
                  vaccination/vet/weight tiles, Dog Log upsell, and
                  upcoming-appointment surfacing. Hides itself gracefully
                  if the API returns 403 (non-owner viewer). */}
              {isOwnerOrAdmin && <HealthVault dogId={id} />}
              <SubList
                title="Health records"
                items={dog.health_records || []}
                fields={[
                  { name: "date", label: "Date", type: "date", required: true },
                  { name: "type", label: "Type", required: true },
                  { name: "description", label: "Description", required: true },
                  { name: "vet", label: "Veterinarian" },
                ]}
                render={(h) => (
                  <>
                    <div className="font-medium">{h.type}</div>
                    <div className="text-xs text-[#6B6B6B]">
                      {formatDate(h.date)} · {h.description} {h.vet && `· ${h.vet}`}
                    </div>
                  </>
                )}
                onAdd={(data) => addSubItem("health-records", data)}
                onDelete={(itemId) => delSubItem("health-records", itemId)}
                addLabel="Add health record"
                dogId={id}
                attachmentKind="health-records"
                canEdit={isOwnerOrAdmin}
                onAttachmentsChanged={load}
              />
            </TabsContent>

            <TabsContent value="ownership" className="mt-6">
              <SubList
                title="Ownership history"
                items={dog.ownership_history || []}
                fields={[
                  { name: "owner_name", label: "Owner", required: true },
                  { name: "start_date", label: "From", type: "date", required: true },
                  { name: "end_date", label: "To", type: "date" },
                  { name: "notes", label: "Notes" },
                ]}
                render={(o) => (
                  <>
                    <div className="font-medium">{o.owner_name}</div>
                    <div className="text-xs text-[#6B6B6B]">
                      {formatDate(o.start_date)} — {o.end_date ? formatDate(o.end_date) : "present"}
                      {o.notes && ` · ${o.notes}`}
                    </div>
                  </>
                )}
                onAdd={(data) => addSubItem("ownership", data)}
                onDelete={(itemId) => delSubItem("ownership", itemId)}
                addLabel="Add owner"
                dogId={id}
                attachmentKind="ownership"
                canEdit={isOwnerOrAdmin}
                onAttachmentsChanged={load}
              />
              {isOwnerOrAdmin && (
                <DogNotificationsPanel dogId={id} dogName={dog.name} />
              )}
            </TabsContent>

            {isOwnerOrAdmin && (
              <TabsContent value="care" className="mt-6" data-testid="tab-content-care">
                <CareTab dog={dog} isOwnerOrAdmin={isOwnerOrAdmin} />
              </TabsContent>
            )}

            <TabsContent value="breeding" className="mt-6 space-y-6">
              <BreedingPanel
                dog={dog}
                listing={breedingListing}
                isOwner={isOwnerOrAdmin}
                onList={() => setListDialogOpen(true)}
                onDelist={async () => {
                  try {
                    await api.delete(`/breeding/${breedingListing.id}`);
                    toast.success("Listing removed");
                    setBreedingListing(null);
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
              />
              {dog.sex === "female" && (
                <SubList
                  title="Heat cycles"
                  items={dog.heat_cycles || []}
                  fields={[
                    { name: "start_date", label: "Start date", type: "date", required: true },
                    { name: "end_date", label: "End date", type: "date" },
                    { name: "notes", label: "Notes" },
                  ]}
                  render={(h) => (
                    <>
                      <div className="font-medium">{formatDate(h.start_date)}</div>
                      <div className="text-xs text-[#6B6B6B]">
                        {h.end_date ? `Ended ${formatDate(h.end_date)}` : "In progress"}
                        {h.notes && ` · ${h.notes}`}
                      </div>
                    </>
                  )}
                  onAdd={(data) => addSubItem("heat-cycles", data)}
                  onDelete={(itemId) => delSubItem("heat-cycles", itemId)}
                  addLabel="Add heat cycle"
                />
              )}
            </TabsContent>
          </Tabs>

          <ListDogDialog
            open={listDialogOpen}
            onClose={() => setListDialogOpen(false)}
            dogId={id}
          />
          <TransferDialog
            open={transferDialogOpen}
            onClose={() => setTransferDialogOpen(false)}
            dogId={id}
            dogName={dog?.name || "this dog"}
          />
          <AddPedigreeAncestorDialog
            open={addAncestorOpen}
            onClose={() => setAddAncestorOpen(false)}
            dogId={id}
            dogName={dog?.name || "this dog"}
            defaultRelation={addAncestorRelation}
            onSubmitted={(addition) => {
              setMyPendingCount((c) => c + 1);
              if (addition?.id) {
                setPendingPreviewId(addition.id);
                setPreviewAdditionId(addition.id);
              }
              load();
            }}
          />
          <AddPedigreeAncestorDialog
            open={!!resubmitTarget}
            onClose={() => setResubmitTarget(null)}
            dogId={id}
            dogName={dog?.name || "this dog"}
            defaultRelation={resubmitTarget?.relation || "sire"}
            initialEntry={resubmitTarget?.entry || null}
            resubmitId={resubmitTarget?.id || null}
            onSubmitted={() => {
              setResubmitTarget(null);
              load();
            }}
          />
          <CertificatePurchaseDialog
            open={certDialogOpen}
            onClose={() => setCertDialogOpen(false)}
            dogName={dog.name}
            certPrice={certPrice}
            promo={certPromo}
            onPromoApplied={setCertPromo}
            onPromoCleared={() => setCertPromo(null)}
            onCheckout={handleCertificateCheckout}
            purchasing={purchasing}
          />
          <MarketplaceListingDialog
            open={marketplaceDialogOpen}
            onOpenChange={setMarketplaceDialogOpen}
            dog={dog}
            onSaved={(updated) => setDog((d) => ({ ...d, ...updated }))}
          />
          {/* Tentative-pedigree preview dialog. Opened from the
              "Preview tentative pedigree →" link on a pending submission
              card. Was imported but never mounted prior to this fix. */}
          <PedigreeAdditionPreview
            additionId={previewAdditionId}
            open={Boolean(previewAdditionId)}
            onClose={() => setPreviewAdditionId(null)}
          />
          <AdminTools
            recordType="dog"
            recordId={dog.id}
            recordName={dog.name || dog.id}
            onDeleted={() => navigate("/dogs")}
            onMerged={(canonical) => navigate(`/dogs/${canonical}`)}
          />
          {/* Pedigree-certificate PDF modal — portal overlay (not a
              route), so closing it doesn't depend on the iOS PWA
              history stack. See PdfModal.jsx. */}
          <PdfModal
            open={showPedigreePdf}
            onClose={() => setShowPedigreePdf(false)}
            src={certUrl}
            title={`Pedigree certificate · ${dog?.name || "Dog"}`}
            downloadName={`pedigree-${(dog?.name || id).replace(/\s+/g, "-").toLowerCase()}.pdf`}
          />
        </div>
      </div>
    </AppShell>
  );
}
